/**
 * <product-hero> — behaviour for sections/product-hero.liquid.
 *
 * - Variant switching: the size swatches are radio inputs named "id",
 *   so with JS disabled the form still submits the chosen variant.
 *   With JS, changing one updates the price, the add-to-cart button
 *   state, and the URL's ?variant= param.
 * - Add to cart: posts to /cart/add.js, then asks the cart drawer to
 *   refresh itself and slide open. Errors land in an aria-live region.
 * - Wishlist: a localStorage-only toggle (no app), keyed by product
 *   handle, persisted per browser.
 *
 * Vanilla ES module, no dependencies.
 */

const WISHLIST_KEY = 'jdv-wishlist';

class ProductHero extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.priceEl = this.querySelector('[data-price]');
    this.errorEl = this.querySelector('[data-error]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.wishlistButton = this.querySelector('[data-wishlist]');

    this.variants = this.readJson('[data-variants]') ?? {};
    this.strings = this.readJson('[data-strings]') ?? {};

    // connectedCallback re-runs every time the element is re-attached
    // (live reload, theme editor) — bind listeners exactly once, and
    // delegate to `this` so they survive inner re-renders.
    if (!this.listenersBound) {
      this.listenersBound = true;

      this.addEventListener('change', (event) => {
        if (event.target.name === 'id') this.onVariantChange(event.target.value);
      });
      this.addEventListener('submit', (event) => {
        if (event.target === this.form) this.onSubmit(event);
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-wishlist]')) this.toggleWishlist();
      });
    }

    this.renderWishlist();
    this.initDrop();
  }

  readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  onVariantChange(variantId) {
    const variant = this.variants[variantId];
    if (!variant) return;

    if (this.priceEl) this.priceEl.textContent = variant.price;

    if (this.addButton && this.addLabel) {
      // While a drop countdown is pending, the button stays locked no
      // matter which size is picked
      this.addButton.disabled = !variant.available || this.dropPending === true;
      this.addLabel.textContent = variant.available ? this.strings.addToCart : this.strings.soldOut;
    }

    // Keep the URL shareable: /?variant=123 (per-spec, works on any page)
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url);
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || this.addButton?.disabled || this.dropPending === true) return;

    this.setError('');
    this.addButton?.setAttribute('aria-busy', 'true');
    if (this.addButton) this.addButton.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      const data = await response.json();

      if (!response.ok) {
        // Shopify's error payloads carry a human-readable `description`
        this.setError(data.description || this.strings.error);
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:added'));
    } catch {
      this.setError(this.strings.error);
    } finally {
      this.addButton?.removeAttribute('aria-busy');
      if (this.addButton) {
        // Re-enable unless the selected variant is sold out
        const checked = this.form.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
        const variant = checked ? this.variants[checked.value] : null;
        this.addButton.disabled = variant ? !variant.available : false;
      }
    }
  }

  setError(message) {
    if (this.errorEl) this.errorEl.textContent = message;
  }

  /* ---- Drop countdown ----
     The section renders the locked/unlocked state for its render time;
     this keeps it accurate to the second in the visitor's browser and
     flips the button live at the drop moment, no reload needed. */

  initDrop() {
    this.dropEl = this.querySelector('[data-drop]');
    this.dropDigits = this.querySelector('[data-drop-digits]');
    this.dropPending = false;

    const iso = this.dataset.dropTime;
    if (!this.dropEl || !iso) return;

    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return; // unparseable setting: fail open

    // connectedCallback can re-run on re-attach — never stack timers
    if (this.dropTimer) clearInterval(this.dropTimer);

    const tick = () => {
      const remaining = target - Date.now();

      if (remaining <= 0) {
        this.unlockDrop();
        return;
      }

      // Lock (also corrects a stale cached page that rendered unlocked)
      this.dropPending = true;
      this.dropEl.hidden = false;
      if (this.addButton) {
        this.addButton.hidden = true;
        this.addButton.disabled = true;
      }
      if (this.dropDigits) this.dropDigits.textContent = this.formatDropTime(remaining);
    };

    tick();
    if (this.dropPending) this.dropTimer = setInterval(tick, 1000);
  }

  unlockDrop() {
    if (this.dropTimer) clearInterval(this.dropTimer);
    this.dropPending = false;

    if (this.dropEl) this.dropEl.hidden = true;
    if (this.addButton) {
      this.addButton.hidden = false;
      // Enable only if the selected variant is actually in stock
      const checked = this.form?.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
      const variant = checked ? this.variants[checked.value] : null;
      this.addButton.disabled = variant ? !variant.available : false;
    }
  }

  formatDropTime(ms) {
    const total = Math.floor(ms / 1000);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return days > 0
      ? `${days}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  /* ---- Wishlist (localStorage only — no app) ---- */

  toggleWishlist() {
    const handle = this.wishlistButton?.dataset.productHandle;
    if (!handle) return;

    const list = this.readWishlist();
    const next = list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle];
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
    } catch {
      // Storage full or blocked — the toggle still works for this page view
    }
    this.renderWishlist();
  }

  readWishlist() {
    try {
      const parsed = JSON.parse(localStorage.getItem(WISHLIST_KEY));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  renderWishlist() {
    const handle = this.wishlistButton?.dataset.productHandle;
    if (!handle) return;

    const saved = this.readWishlist().includes(handle);
    this.wishlistButton.setAttribute('aria-pressed', String(saved));
    this.wishlistButton.classList.toggle('is-saved', saved);
    const label = this.wishlistButton.querySelector('[data-wishlist-label]');
    if (label) label.textContent = saved ? this.strings.wishlistAdded : this.strings.wishlistAdd;
  }
}

if (!customElements.get('product-hero')) {
  customElements.define('product-hero', ProductHero);
}
